import React, { Children, useCallback, useState } from "react";
import Table from "react-bootstrap/Table";
import styled, { withTheme, css } from "styled-components";
import { debounce } from "../../../modules/utils";
import "./DefaultTable.css";
import { tablePageSizes, orderType } from "./constants";
import TablePagination from "./TablePagination";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import LoadingSpinner from "../../ui-library/LoadingSpinner/LoadingSpinner";

export const ExpandableLine = (props) => {
  return props.children;
};

export const Line = (props) => {
  return props.children;
};

export const Filter = (props) => {
  return props.children;
};

export const ActionButtons = (props) => {
  return props.children;
};

const ButtonWrapper = styled.div`
  display: flex;
  float: right;
  @media (max-width: 767px) {
    float: left;
  }
`;

const NoDataLabel = styled.span`
  font-size: 14px;
`;

export const ExpandableLines = (props) => {
  const { children, headers } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  const drawChild = (child, i) => (
    <tr key={`c-${i}`}>
      {child && <StyledLine></StyledLine>}
      {child?.props?.children?.map((child, index) => (
        <StyledLine
          isChild
          responsivetable={1}
          header={headers[index].label}
          key={index}
        >
          {child}
        </StyledLine>
      ))}
    </tr>
  );

  const renderChildren = (c) => {
    if (Array.isArray(c)) {
      return c.map((elem, i) => drawChild(elem, i));
    }
  };

  let restOfChildren = [...children];
  restOfChildren.splice(0, 1);

  const showArrow = (children) => {
    return children && children.length > 0 && Array.isArray(children[1]);
  };

  return (
    <ExpandableLine>
      <StyledParentRow
        {...children[0]?.props}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <td>
          <span>
            {showArrow(children) && (
              <i className={`fas fa-angle-${isExpanded ? "up" : "down"}`}></i>
            )}
          </span>
        </td>
        {children[0]?.props?.children?.map((child, index) => (
          <StyledLine
            responsivetable={1}
            header={headers[index].label}
            key={index}
          >
            {child}
          </StyledLine>
        ))}
      </StyledParentRow>

      {isExpanded && restOfChildren.map((c, index) => renderChildren(c))}
    </ExpandableLine>
  );
};

const TableActions = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const FiltersComponent = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: flex-start;
  max-width: fit-content;

  div {
    margin-right: 10px;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    div {
      margin-bottom: 5px;
    }
  }
`;
const ActionButtonsComponent = styled.div`
  margin-left: auto;

  @media (max-width: 767px) {
    margin-left: unset;
  }
`;

const StyledTable = styled(Table)`
  span {
    display: block;
  }

  th i.silver {
    color: ${(props) => props.theme?.silver};
  }

  td {
    font-size: 0.75rem;
    vertical-align: middle;
  }

  thead th,
  thead td {
    border-top: 1px transparent;
    font-weight: ${(props) => (props.$smallTable ? "600" : "bold")};
    font-size: ${(props) => (props.$smallTable ? "0.75rem" : "")};
  }

  th,
  td {
    ${(props) =>
      props.$noLineBorder
        ? "border-top: none;"
        : "border-top: 1px solid #dee2e6;"};
    padding: ${(props) => (props.$smallTable ? "0.20rem" : "")};
  }

  thead th {
    ${(props) =>
      props.$noLineBorder
        ? "border-bottom: none;"
        : "border-bottom: 2px solid #dee2e6;"}
  }

  ${(props) =>
    props?.responsivetable === 1 &&
    css`
      @media only screen and (max-width: 760px),
        (min-device-width: 768px) and (max-device-width: 1024px) {
        /* Force table to not be like tables anymore */
        table,
        thead,
        tbody,
        th,
        td,
        tr {
          display: block;
        }

        /* Hide table headers (but not display: none;, for accessibility) */
        thead tr {
          position: absolute;
          top: -9999px;
          left: -9999px;
        }

        tr {
          margin: 0 0 1rem 0;
        }

        tr:nth-child(odd) {
          background: ${(props) =>
            props.isExpandable ? props.theme?.whiteSmoke : ""};
        }

        td {
          /* Behave  like a "row" */
          border: none;
          border-bottom: 1px solid #eee;
          position: relative;
          padding-left: 50%;
          word-wrap: break-word;
        }
      }
    `}
`;

const StyledParentRow = styled.tr`
  background-color: ${(props) => props.theme?.whiteSmoke};
  cursor: pointer;
`;

const StyledLine = styled.td`
  ${(props) =>
    props.isChild &&
    css`
      background-color: ${(props) => props.theme?.white};
    `}
  ${(props) =>
    props?.responsivetable === 1 &&
    css`
      @media only screen and (max-width: 760px),
        (min-device-width: 768px) and (max-device-width: 1024px) {
        :before {
          content: "${(props) => props?.header}";
          /* Top/left values mimic padding */
          position: absolute;
          left: 6px;
          width: 45%;
          padding-right: 10px;
          white-space: nowrap;
          font-weight: bold;
        }

        div {
          display: inline-block;
          margin-top: -6px;
        }

        span {
          word-break: break-all;
        }
      }
    `}
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const DefaultTable = (props) => {
  const {
    headers,
    children,
    hasSearch,
    haspagination,
    haspagesizefilter,
    maincolor,
    totalitems,
    getData,
    searchedParams,
    noLineBorder,
    topControlsClass,
    smallTable,
    responsivetable,
    isExpandable,
    loading,
    noDataText,
  } = props;

  const { t } = useTranslation();

  const { page, limit, search, orderBy, order, filter } = searchedParams;
  const [searchedWord, setSearchedWord] = useState(searchedParams.search);

  const changePageSizeHandler = (value) => {
    const params = {
      ...searchedParams,
      page: 0,
      filter: filter,
      limit: value,
      search: search,
      orderBy: orderBy,
      order: order,
    };
    getData(params);
  };

  const onSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const params = {
        ...searchedParams,
        page: page,
        limit: limit,
        filter: filter,
        search: event.target.value,
        orderBy: orderBy,
        order: order,
      };
      getData(params);
    }
  };

  const clearInput = () => {
    setSearchedWord("");
    const params = {
      ...searchedParams,
      page: 0,
      limit: limit,
      filter: filter,
      search: "",
      orderBy: orderBy,
      order: order,
    };
    getData(params);
  };

  const onChangeSearch = (value) => {
    setSearchedWord(value);
    searchOnTable(value);
  };

  const searchOnTable = useCallback(
    debounce((value) => {
      const params = {
        ...searchedParams,
        page: 0,
        limit: limit,
        filter: filter,
        search: value,
        orderBy: orderBy,
        order: order,
      };
      getData(params);
    }, 500),
    [getData, searchedParams]
  );

  const isActive = (header, orderBy, order, orderType) => {
    if (header.key !== orderBy || order !== orderType) {
      return "silver";
    }
    return "";
  };

  const onSortHandler = (header) => {
    if (header.isSortable) {
      const params = {
        ...searchedParams,
        page: 0,
        limit: limit,
        filter: filter,
        search: search,
        orderBy: header.key,
        order: order === orderType.ASC ? orderType.DESC : orderType.ASC,
      };
      getData(params);
    }
  };

  const renderActionButtons = (child) => {
    if (child?.type === ActionButtons) {
      const { children } = child.props;
      return (
        <ButtonWrapper>
          {children && (
            <ActionButtons>
              {Children.map(children, (child) => (
                <div>{child}</div>
              ))}
            </ActionButtons>
          )}
        </ButtonWrapper>
      );
    }
  };

  const renderFilter = (child) => {
    if (child && child.type === Filter) {
      const { children } = child.props;
      return (
        <Filter>
          <div>
            {Children.map(children, (child) => (
              <span>{child}</span>
            ))}
          </div>
        </Filter>
      );
    }
  };

  const renderLine = (child) => {
    const renderInfo = (data) => {
      if (data?.props?.children !== null && data?.props?.children !== "") {
        return data;
      }
      return "-";
    };

    if (child) {
      const { children } = child.props;

      if (child.type === Line) {
        return (
          <Line>
            <tr>
              {children?.map((child, index) => (
                <StyledLine
                  key={index}
                  {...{ index }}
                  responsivetable={responsivetable}
                  header={headers[index].label}
                >
                  {renderInfo(child)}
                </StyledLine>
              ))}
            </tr>
          </Line>
        );
      } else if (child.type === ExpandableLine) {
        return <ExpandableLines {...{ children }} {...{ headers }} />;
      }
    }
  };

  return (
    <>
      <TableActions className={`${topControlsClass ? topControlsClass : ""}`}>
        <div className="pl-0 pb-2 mr-4">
          {hasSearch ? (
            <SearchContainer>
              <input
                data-testid="searchComponent"
                className="form-control customSearch"
                type="text"
                placeholder={t("appText.table.search")}
                value={searchedWord}
                onKeyDown={(e) => onSearchKeyDown(e)}
                onChange={(e) => onChangeSearch(e.target.value)}
              />
              {searchedWord && searchedWord !== "" && (
                <button
                  data-testid="clearBtn"
                  className="bg-transparent border-0 ml-2"
                  onClick={() => clearInput()}
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </SearchContainer>
          ) : null}
        </div>
        <FiltersComponent className="pb-2 pl-0">
          {Children?.map(children, (child) => renderFilter(child))}
        </FiltersComponent>
        <ActionButtonsComponent className="px-0">
          {Children?.map(children, (child) => renderActionButtons(child))}
        </ActionButtonsComponent>
      </TableActions>
      <div className={`row ${smallTable ? "px-3" : ""}`}>
        <StyledTable
          data-testid="defaultTable"
          responsive
          $noLineBorder={noLineBorder}
          $smallTable={smallTable}
          responsivetable={responsivetable}
        >
          <thead>
            <tr>
              {isExpandable && <th></th>}
              {headers &&
                headers.map((header, index) => (
                  <th
                    key={index}
                    data-testid={`headerSortable-${index} `}
                    className={`${header.isSortable ? "cursor-pointer" : ""} `}
                    onClick={() => onSortHandler(header)}
                  >
                    <span>{header.label}</span>
                    {header.isSortable && (
                      <>
                        <i
                          className={`fas fa-caret-up ml-3 mr-2 ${isActive(
                            header,
                            orderBy,
                            order,
                            orderType.ASC
                          )}`}
                        />
                        <i
                          className={`fas fa-caret-down ${isActive(
                            header,
                            orderBy,
                            order,
                            orderType.DESC
                          )}`}
                        />
                      </>
                    )}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {loading || totalitems === null ? (
              <tr>
                <td colSpan={headers?.length.toString()}>
                  <LoadingSpinner />
                </td>
              </tr>
            ) : totalitems === 0 && noDataText ? (
              <tr>
                <td colSpan={headers?.length.toString()}>
                  <NoDataLabel>{noDataText}</NoDataLabel>
                </td>
              </tr>
            ) : (
              Children?.map(children, (child) => renderLine(child))
            )}
          </tbody>
        </StyledTable>
      </div>
      <div className="row">
        <div className="col-sm-6">
          {haspagesizefilter ? (
            <form
              data-testid="pageSizeComponent"
              className="form-inline mb-1 justify-content-start"
            >
              {t("appText.table.showUpTo")}
              <CustomDropdown
                className="mr-2 ml-2"
                defaultValue={searchedParams?.limit}
                values={tablePageSizes}
                onChange={(e) => changePageSizeHandler(e)}
              />
              {t("appText.items")}
            </form>
          ) : null}
        </div>
        <div className="col-sm-6">
          {haspagination ? (
            <TablePagination
              specificKey={null}
              specificParams={searchedParams}
              maincolor={maincolor}
              total={totalitems}
              getData={getData}
              small={smallTable}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

DefaultTable.defaultProps = {
  responsivetable: 1,
  isExpandable: false,
};

DefaultTable.propTypes = {
  responsivetable: PropTypes.number,
  isExpandable: PropTypes.bool,
};

export default withTheme(DefaultTable);
